import { useCallback, useEffect, useRef, useState } from "react";
import { authStore } from "@/stores/auth-store";

/**
 * Configuration options for the inactivity timeout hook
 */
export interface InactivityTimeoutOptions {
	/** Time before showing warning modal (in milliseconds) */
	warningTimeout?: number;
	/** Total time before automatic logout (in milliseconds) */
	logoutTimeout?: number;
	/** Whether the timeout system is enabled */
	enabled?: boolean;
	/** Callback when warning phase begins */
	onWarning?: () => void;
	/** Callback when logout occurs */
	onLogout?: () => void;
	/** Events to listen for user activity */
	events?: string[];
}

/**
 * State interface for inactivity timeout
 */
export interface InactivityState {
	/** Whether user is currently idle (past warning threshold) */
	isIdle: boolean;
	/** Whether warning modal should be shown */
	isWarning: boolean;
	/** Time remaining before logout (in milliseconds) */
	timeRemaining: number;
	/** Timestamp of last detected activity */
	lastActivity: number;
	/** Whether system is currently active/monitoring */
	isActive: boolean;
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG = {
	warningTimeout: 25 * 60 * 1000, // 25 minutes
	logoutTimeout: 30 * 60 * 1000, // 30 minutes
	enabled: true,
	events: [
		"mousemove",
		"mousedown",
		"resize",
		"keydown",
		"touchstart",
		"wheel",
		"scroll",
		"click",
	],
} as const;

/**
 * Enhanced inactivity timeout hook with two-stage warning system
 *
 * This hook monitors user activity and provides a two-stage timeout system:
 * 1. Warning stage: Shows modal with countdown when warningTimeout is reached
 * 2. Logout stage: Automatically logs out user when logoutTimeout is reached
 *
 * Based on the provided useIdle example but enhanced with:
 * - Two-stage timeout system
 * - Real-time countdown timer
 * - Auth store integration
 * - Configurable event monitoring
 * - Proper cleanup and memory management
 */
export function useInactivityTimeout(
	options: InactivityTimeoutOptions = {},
): InactivityState & { resetActivity: () => void } {
	const {
		warningTimeout = DEFAULT_CONFIG.warningTimeout,
		logoutTimeout = DEFAULT_CONFIG.logoutTimeout,
		enabled = DEFAULT_CONFIG.enabled,
		onWarning,
		onLogout,
		events = DEFAULT_CONFIG.events,
	} = options;

	// State management
	const [state, setState] = useState<InactivityState>({
		isIdle: false,
		isWarning: false,
		timeRemaining: 0,
		lastActivity: Date.now(),
		isActive: false,
	});

	// Timer references for cleanup
	const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
	const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
	const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

	// Separate ref to track last activity reset time
	const lastResetTimeRef = useRef<number>(Date.now());

	// Clear all timers utility
	const clearAllTimers = useCallback(() => {
		if (warningTimerRef.current) {
			clearTimeout(warningTimerRef.current);
			warningTimerRef.current = null;
		}
		if (logoutTimerRef.current) {
			clearTimeout(logoutTimerRef.current);
			logoutTimerRef.current = null;
		}
		if (countdownTimerRef.current) {
			clearTimeout(countdownTimerRef.current);
			countdownTimerRef.current = null;
		}
	}, []);

	// Start countdown timer for warning phase
	const startCountdown = useCallback(() => {
		const warningDuration = logoutTimeout - warningTimeout;
		let remaining = warningDuration;

		const updateCountdown = () => {
			setState((prev) => ({
				...prev,
				timeRemaining: remaining,
			}));

			remaining -= 1000;

			if (remaining > 0) {
				countdownTimerRef.current = setTimeout(updateCountdown, 1000);
			}
		};

		// Start the countdown immediately
		updateCountdown();
	}, [logoutTimeout, warningTimeout]);

	// Handle warning timeout
	const handleWarningTimeout = useCallback(() => {
		console.log("⚠️ Inactivity: Warning timeout reached");

		setState((prev) => ({
			...prev,
			isIdle: true,
			isWarning: true,
		}));

		// Start countdown timer
		startCountdown();

		// Set logout timer for remaining time
		const remainingTime = logoutTimeout - warningTimeout;
		logoutTimerRef.current = setTimeout(() => {
			console.log("🔒 Inactivity: Logout timeout reached - logging out user");

			// Clear warning state
			setState((prev) => ({
				...prev,
				isWarning: false,
				timeRemaining: 0,
			}));

			// Trigger logout callback
			onLogout?.();

			// Directly call auth store logout
			authStore.getState().logout();
		}, remainingTime);

		// Trigger warning callback
		onWarning?.();
	}, [logoutTimeout, warningTimeout, onWarning, onLogout, startCountdown]);

	// Reset activity and timers
	const resetActivity = useCallback(() => {
		const now = Date.now();

		// Clear all existing timers
		clearAllTimers();

		// Reset state
		setState((prev) => ({
			...prev,
			isIdle: false,
			isWarning: false,
			timeRemaining: 0,
			lastActivity: now,
		}));

		// Only start new timer if enabled
		if (enabled) {
			warningTimerRef.current = setTimeout(
				handleWarningTimeout,
				warningTimeout,
			);
		}
	}, [enabled, warningTimeout, handleWarningTimeout, clearAllTimers]);

	// Reset activity only if not currently in warning phase
	const resetActivityIfNotWarning = useCallback(() => {
		setState((prev) => {
			// If we're already in warning phase, don't reset
			if (prev.isWarning) {
				return prev;
			}

			const now = Date.now();

			// Clear all existing timers
			clearAllTimers();

			// Reset state and restart timer
			if (enabled) {
				warningTimerRef.current = setTimeout(
					handleWarningTimeout,
					warningTimeout,
				);
			}

			return {
				...prev,
				isIdle: false,
				isWarning: false,
				timeRemaining: 0,
				lastActivity: now,
			};
		});
	}, [enabled, warningTimeout, handleWarningTimeout, clearAllTimers]);

	// Throttled activity handler to prevent excessive timer resets
	const handleActivity = useCallback(() => {
		const now = Date.now();

		// Throttle to once per second
		if (now - lastResetTimeRef.current > 1000) {
			resetActivityIfNotWarning();
			lastResetTimeRef.current = now;
		}
	}, [resetActivityIfNotWarning]);

	// Handle visibility change (tab focus/blur)
	const handleVisibilityChange = useCallback(() => {
		if (!document.hidden) {
			handleActivity();
		}
	}, [handleActivity]);

	// Main effect for setting up event listeners and timers
	useEffect(() => {
		if (!enabled) {
			clearAllTimers();
			setState((prev) => ({
				...prev,
				isActive: false,
				isIdle: false,
				isWarning: false,
				timeRemaining: 0,
			}));
			return;
		}

		// console.log("🎯 Inactivity: Starting timeout monitoring", {
		// 	warningTimeout: warningTimeout / 1000 / 60,
		// 	logoutTimeout: logoutTimeout / 1000 / 60,
		// 	events,
		// });

		// Mark as active
		setState((prev) => ({
			...prev,
			isActive: true,
		}));

		// Start initial warning timer
		warningTimerRef.current = setTimeout(handleWarningTimeout, warningTimeout);

		// Add event listeners for user activity
		for (const event of events) {
			window.addEventListener(event, handleActivity);
		}

		// Add visibility change listener
		document.addEventListener("visibilitychange", handleVisibilityChange);

		// Cleanup function
		return () => {
			// console.log("🧹 Inactivity: Cleaning up timeout monitoring");

			// Remove event listeners
			for (const event of events) {
				window.removeEventListener(event, handleActivity);
			}
			document.removeEventListener("visibilitychange", handleVisibilityChange);

			// Clear all timers
			clearAllTimers();

			// Mark as inactive
			setState((prev) => ({
				...prev,
				isActive: false,
			}));
		};
	}, [
		enabled,
		warningTimeout,
		// logoutTimeout,
		events,
		handleActivity,
		handleVisibilityChange,
		handleWarningTimeout,
		clearAllTimers,
	]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			clearAllTimers();
		};
	}, [clearAllTimers]);

	return {
		...state,
		resetActivity,
	};
}

/**
 * Utility function to format milliseconds into MM:SS format
 */
export function formatTimeRemaining(milliseconds: number): string {
	const totalSeconds = Math.ceil(milliseconds / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
